import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { type SuccessResponse } from '@vestigas/data';

import { triggerSync } from './triggerSync';
import { type TriggerSyncParams } from './types';

/**
 * Custom hook for triggering data synchronization between environments.
 *
 * @param options - Optional configuration for the mutation
 * @returns A mutation object that can be used to trigger the sync operation
 *
 * @example
 * ```tsx
 * const { mutate, isLoading } = useMutationTriggerSync();
 *
 * // Trigger sync
 * mutate({
 *   syncSettings: {
 *     companyId: '123e4567-e89b-12d3-a456-426614174000',
 *     env: 'prod'
 *   },
 *   targetEnvironment: 'staging'
 * });
 * ```
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Sync/operation/trigger_sync_sync_trigger_post
 */
export const useMutationTriggerSync = (
  options?: Omit<
    UseMutationOptions<SuccessResponse, Error, TriggerSyncParams>,
    'mutationFn'
  >,
) => {
  return useMutation<SuccessResponse, Error, TriggerSyncParams>({
    mutationFn: triggerSync,
    ...options,
  });
};
